import type { BadgeCardProps } from '@pandiafi/components'
import type { TFunction } from 'i18next'

import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'
import {
  fmtCurrency,
  priceForDate,
  priceForToday,
  type PricePeriod,
  normDate,
} from './dayReservableAreas.helpers'

import { todayISO } from 'utils/dateUtils'

type BadgeStatus = NonNullable<BadgeCardProps['status']>

const statusI18nKey: Record<
  BadgeStatus,
  | 'legend.available'
  | 'legend.reservedOrInactive'
  | 'legend.mine'
  | 'legend.mineUpcoming'
  | 'legend.notInUse'
  | 'legend.ending'
  | 'legend.starting'
> = {
  default: 'legend.available',
  disabled: 'legend.reservedOrInactive',
  active: 'legend.mine',
  upcoming: 'legend.mineUpcoming',
  'not-in-use': 'legend.notInUse',
  ending: 'legend.ending',
  starting: 'legend.starting',
} as const

export const areaStatusLabel = (
  status: BadgeCardProps['status'] | undefined,
  t: TFunction<'storage'>
): string | undefined => {
  if (!status) return undefined
  const key = statusI18nKey[status as BadgeStatus]
  return key ? t(key) : String(status)
}

export const areaDisplayId = (area: CoreDayReservableArea): string =>
  area.locationDetails ?? String(area.rentableHierarchyId ?? '')

export const areaTitle = (area: CoreDayReservableArea): string => {
  return `${area.additionalDetails ?? ''} ${area.locationDetails ?? ''}`.trim()
}

export const areaIcons = (
  _area: CoreDayReservableArea
): {
  primary: BadgeCardProps['icon']
  secondary?: BadgeCardProps['icon']
} => {
  return { primary: 'key' }
}

export const areaPriceFor = (
  area: CoreDayReservableArea,
  atISO?: string | null
): number | undefined => {
  const pList = (area.prices ?? []) as PricePeriod[] | undefined
  return priceForDate(pList, atISO ?? null) ?? priceForToday(pList)
}

export const areaPriceLabel = (
  area: CoreDayReservableArea,
  locale: string,
  atISO?: string | null
): string | undefined => {
  const price = areaPriceFor(area, atISO)
  return typeof price === 'number' ? fmtCurrency(price, locale) : undefined
}

export const areaAddressLabel = (area: CoreDayReservableArea): string => {
  const addr = area.buildingPartAddress
  return addr ? `${addr.streetName} ${addr.streetNumber}, ${addr.city}` : ''
}

export const findActiveReservation = (
  area: CoreDayReservableArea,
  today = todayISO()
) =>
  (area.reservations ?? []).find((r) => {
    const s = normDate(r.startDate) ?? '0000-01-01'
    const e = normDate(r.endDate) ?? '9999-12-31'
    return s <= today && today <= e
  })

export const findFutureReservation = (
  area: CoreDayReservableArea,
  today = todayISO()
) =>
  (area.reservations ?? [])
    .filter((r) => {
      const s = normDate(r.startDate)
      return !!s && s > today
    })
    .sort((a, b) => {
      const as = normDate(a.startDate) ?? '9999-12-31'
      const bs = normDate(b.startDate) ?? '9999-12-31'
      return as.localeCompare(bs)
    })[0]

export const formatDate = (iso: string, locale = 'fi-FI') => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale)
}
